import { createSetter } from 'rewire-common';

import { Form, IFormContext, ActionType } from 'rewire-ui';
import { DeepPartial, EmployeeUpdateInput, IEmployee } from '../DataTypes';
import DataMutations from '../graphql/Mutations';
import DataQueries from '../graphql/Queries';
import { notifications } from '../ui/Notifications';
import SelectedRowWatcher from '../utility/SelectedRowWatcher';
import timeOffBalancesDialogModel from './TimeOffBalancesDialogModel';
import scheduleInfoPanelModel from './ScheduleInfoPanelModel';
import scheduleModel from './ScheduleModel';
import mainPageModel from '../main-navigation/MainPageModel';
import { UdfsContainerModel } from './UdfsModel';
import employeeViewTypeModel from '../settings/EmployeeViewTypeModel';
import auditLogDialogModel from '../audit/AuditLogDialogModel';
import archiveDeleteEmployeeDialogModel from '../settings/ArchiveDeleteEmployeeDialogModel';
import { IAuditLogFilters } from '../audit/AuditLogListModel';

interface IInfoPanelEmployeeViewModelState {
  employee?: IEmployee;
  jobId?: string;
}

export class InfoPanelEmployeeViewModel extends UdfsContainerModel<IInfoPanelEmployeeViewModelState> {
  selectedRowWatcher: SelectedRowWatcher = new SelectedRowWatcher();

  constructor() {
    super(
      '',
      async () => undefined,
      async () => undefined,
      async () => undefined,
      async () => undefined,
      'employees',
      {
        canAdd: () => mainPageModel.canAddEmployees,
        canEdit: () => mainPageModel.canEditEmployees,
        canDelete: () => mainPageModel.canDeleteEmployees,
      },
    );
  }

  protected _createForm(): Form {
    return Form.create(
      (form: IFormContext) => ({
        name: form.string().label('Name').validators('required'),
        code: form.string().label('Code'),
        email: form.email().label('Email'),
        isPayable: form.boolean().label('Is Paid').placeholder('check if employee is payable'),
        phone: form
          .phone()
          .label('Home')
          .accessor((item: any) => item?.phone?.home, createSetter(['phone', 'home'])),
        mobile: form
          .phone()
          .label('Mobile')
          .accessor((item: any) => item?.phone?.mobile, createSetter(['phone', 'mobile'])),
        work: form
          .phone()
          .label('Work')
          .accessor((item: any) => item?.phone?.work, createSetter(['phone', 'work'])),
        fax: form
          .phone()
          .label('Fax')
          .accessor((item: any) => item?.phone?.fax, createSetter(['phone', 'fax'])),
        workFax: form
          .phone()
          .label('Work Fax')
          .accessor((item: any) => item?.phone?.workFax, createSetter(['phone', 'workFax'])),
      }),
      {},
    );
  }

  reset() {
    super.reset();
    this.employee = undefined;
    this.jobId = undefined;
  }

  load: any = async (employee?: IEmployee) => {
    this.isLoading = true;
    this.isModal = false;
    const job = scheduleInfoPanelModel.selectedCell && scheduleModel.getJobFromCell(scheduleInfoPanelModel.selectedCell);
    this.employee = employee;
    this.jobId = job?.id;
    await this.loadView();
    this.isLoading = false;
  };

  loadView = async () => {
    this.state.enable = false;
    if (employeeViewTypeModel.isView('details')) {
      // load employee details
      if (!this.employeeId) {
        this._reset();
      } else {
        const employeeData = await this.getEmployeeData(this.employeeId);
        if (!employeeData) {
          notifications.error('Employee does not exist');
          return;
        }
        const { name, code, email, phone, fields, isPayable } = employeeData;
        this.form.value = this.mapToForm({ name, code, email, phone, fields, isPayable });
      }
    } else {
      // load time off balances
      await timeOffBalancesDialogModel.loadTimeOffTypes(this.employeeId!);
    }
    this.state.enable = true;
  };

  get employee(): IEmployee | undefined {
    return this.state.employee;
  }

  set employee(employee: IEmployee | undefined) {
    this.state.employee = employee;
  }

  get jobId(): string | undefined {
    return this.state.jobId;
  }

  set jobId(jobId: string | undefined) {
    this.state.jobId = jobId;
  }

  get employeeId(): string | undefined {
    return this.employee?.id;
  }

  get hasEmployee(): boolean {
    return !!this.employee;
  }

  get footerActions(): { [index: string]: ActionType } | undefined {
    return { undo: this.actions.undo, save: this.actions.save };
  }

  get isNew() {
    return !this.employeeId;
  }

  getEmployeeData = async (employeeId: string): Promise<DeepPartial<IEmployee | undefined> | undefined> => {
    const returnSchema = `
      id
      name
      code
      phone {
        home
        mobile
        work
        fax
        workFax
      }
      isPayable
      email
      fields
    `;

    const employeesData = await DataQueries.getEmployeesByIds(returnSchema, [employeeId]);
    return employeesData?.[0];
  };

  loadAuditLogDialog = () => {
    const filter = { types: ['employee'] } as IAuditLogFilters;
    if (this.employeeId) {
      filter.entityIds = [this.employeeId];
    }
    auditLogDialogModel.load(filter);
  };

  deleteEmployee = () => {
    if (!this.canDelete || !this.employee) {
      return;
    }
    archiveDeleteEmployeeDialogModel.load(this.employee, 'delete');
  };

  protected async save(): Promise<boolean> {
    if (this.viewOnly || !this.employeeId) {
      return false;
    }

    // if (this.view === 'details') {
    //   timecardData = this.saveDetails();
    // } else {
    //   timecardData = this.saveTimeOffBalances();
    // }

    const newEmployeeValue = this.mapToSave() as EmployeeUpdateInput;
    newEmployeeValue.id = this.employeeId;
    const result = await DataMutations.updateEmployees('', [newEmployeeValue]);
    if (result && result[0]) {
      notifications.success('Employee Updated');
      scheduleInfoPanelModel.submit();
      return true;
    } else {
      notifications.error('Employee Update Failed');
      return false;
    }
  }

  // private getEmployeeValue(employeeValues: ObjectType): EmployeeUpdateInput {
  //   return {
  //     id:        this.employeeId ?? '',
  //     name:      employeeValues.name,
  //     code:      employeeValues.code,
  //     isPayable: employeeValues.isPayable,
  //     fields:    employeeValues.fields,
  //     email:     employeeValues.email,
  //     phone: {
  //       home:    this.form.field.phone.value   && String(this.form.field.phone.value),
  //       mobile:  this.form.field.mobile.value  && String(this.form.field.mobile.value),
  //       work:    this.form.field.work.value    && String(this.form.field.work.value),
  //       fax:     this.form.field.fax.value     && String(this.form.field.fax.value),
  //       workFax: this.form.field.workFax.value && String(this.form.field.workFax.value)
  //     } as PhoneNumberInput
  //   };
  // }
}

const infoPanelEmployeeViewModel = new InfoPanelEmployeeViewModel();
export default infoPanelEmployeeViewModel;
